import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../api/api'

// ── Thunks ──────────────────────────────────────────────────
export const get_admin_giveaways = createAsyncThunk(
    'adminGiveaway/list',
    async ({ page = 1, limit = 20, status = '', organizerType = '', search = '', approvalStatus = '' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            params.set('page', page)
            params.set('limit', limit)
            if (status) params.set('status', status)
            if (organizerType) params.set('organizerType', organizerType)
            if (search) params.set('search', search)
            if (approvalStatus) params.set('approvalStatus', approvalStatus)
            const { data } = await api.get(`/admin/giveaways?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const create_giveaway = createAsyncThunk(
    'adminGiveaway/create',
    async (payload, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post('/admin/giveaways', payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_giveaway = createAsyncThunk(
    'adminGiveaway/getOne',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/giveaways/${id}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const update_giveaway = createAsyncThunk(
    'adminGiveaway/update',
    async ({ id, ...payload }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/giveaways/${id}`, payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const delete_giveaway = createAsyncThunk(
    'adminGiveaway/delete',
    async (id, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.delete(`/admin/giveaways/${id}`)
            return fulfillWithValue({ id, message: data.message })
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const update_giveaway_status = createAsyncThunk(
    'adminGiveaway/updateStatus',
    async ({ id, status }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/giveaways/${id}/status`, { status })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const get_giveaway_participants = createAsyncThunk(
    'adminGiveaway/participants',
    async ({ id, page = 1, limit = 50 }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/giveaways/${id}/participants?page=${page}&limit=${limit}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// Phase 6G — small dashboard-widget summary thunk. Backed by the SAME
// backend endpoint (no new route), but kept in its own slice path so the
// widget and the full analytics page can mount simultaneously without
// fighting over `platformAnalytics`.
export const get_admin_giveaway_dashboard_summary = createAsyncThunk(
    'adminGiveaway/dashboardSummary',
    async (_, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams({
                range: '30d',
                organizerType: 'all',
                status: 'all',
            })
            const { data } = await api.get(`/admin/giveaways/analytics?${params.toString()}`)
            // Only need the summary block for the widget — keep the payload
            // tiny in Redux so we don't carry around top lists we won't show.
            return fulfillWithValue(data?.analytics?.summary || null)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

// Phase 6E — admin platform-wide analytics. Ownership/role enforced at the
// route layer. Errors surface as `analyticsError` for a soft empty state.
export const get_admin_giveaway_analytics = createAsyncThunk(
    'adminGiveaway/platformAnalytics',
    async ({ range = '30d', organizerType = 'all', status = 'all' } = {}, { fulfillWithValue, rejectWithValue }) => {
        try {
            const params = new URLSearchParams()
            params.set('range', range)
            params.set('organizerType', organizerType)
            params.set('status', status)
            const { data } = await api.get(`/admin/giveaways/analytics?${params.toString()}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data || { error: error.message })
        }
    }
)

export const approve_giveaway = createAsyncThunk(
    'adminGiveaway/approve',
    async ({ id, decision, reason }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.patch(`/admin/giveaways/${id}/approval`, { decision, reason })
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// Phase 6H — winner audit history list + rollback.
export const get_winner_history = createAsyncThunk(
    'adminGiveaway/winnerHistory',
    async ({ id, page = 1, limit = 20 }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.get(`/admin/giveaways/${id}/winner-history?page=${page}&limit=${limit}`)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const rollback_winner_history = createAsyncThunk(
    'adminGiveaway/rollbackWinnerHistory',
    async ({ id, historyId, rollbackTo = 'previous', clearWinners = false, notifyNewWinners = false }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const { data } = await api.post(
                `/admin/giveaways/${id}/winner-history/${historyId}/rollback`,
                { rollbackTo, clearWinners, notifyNewWinners },
            )
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

export const select_giveaway_winners = createAsyncThunk(
    'adminGiveaway/selectWinners',
    async ({ id, mode, count, participantIds }, { fulfillWithValue, rejectWithValue }) => {
        try {
            const payload = { mode }
            if (mode === 'random') payload.count = count
            else payload.participantIds = participantIds
            const { data } = await api.post(`/admin/giveaways/${id}/select-winners`, payload)
            return fulfillWithValue(data)
        } catch (error) {
            return rejectWithValue(error.response?.data)
        }
    }
)

// ── Slice ───────────────────────────────────────────────────
const adminGiveawaySlice = createSlice({
    name: 'adminGiveaway',
    initialState: {
        giveaways:        [],
        total:            0,
        page:             1,
        totalPages:       1,
        currentGiveaway:  null,
        participants:     [],
        participantsTotal: 0,
        winners:          [],
        loader:           false,
        submitLoader:     false,
        successMessage:   '',
        errorMessage:     '',
        // Phase 6E — platform-wide analytics cache.
        platformAnalytics:        null,
        platformAnalyticsLoader:  false,
        platformAnalyticsError:   '',
        // Phase 6G — main-dashboard widget summary (last 30 days, no filters).
        dashboardAnalytics:        null,
        dashboardAnalyticsLoader:  false,
        dashboardAnalyticsError:   '',
        // Phase 6H — winner audit history.
        winnerHistory:            [],
        winnerHistoryTotal:       0,
        winnerHistoryPage:        1,
        winnerHistoryTotalPages:  1,
        winnerHistoryLoader:      false,
        rollbackLoader:           false,
    },
    reducers: {
        messageClear: (state) => { state.successMessage = ''; state.errorMessage = '' },
        clearCurrent: (state) => { state.currentGiveaway = null; state.participants = []; state.winners = [] },
        clearPlatformAnalytics: (state) => {
            state.platformAnalytics = null
            state.platformAnalyticsError = ''
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(get_admin_giveaways.pending,   (s) => { s.loader = true })
            .addCase(get_admin_giveaways.fulfilled, (s, { payload }) => {
                s.loader = false
                s.giveaways = payload.giveaways || []
                s.total = payload.total || 0
                s.page = payload.page || 1
                s.totalPages = payload.totalPages || 1
            })
            .addCase(get_admin_giveaways.rejected,  (s) => { s.loader = false })

            .addCase(create_giveaway.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(create_giveaway.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Yaradıldı'
                s.currentGiveaway = payload.giveaway || null
            })
            .addCase(create_giveaway.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Xəta baş verdi'
            })

            .addCase(get_giveaway.fulfilled, (s, { payload }) => { s.currentGiveaway = payload.giveaway })

            .addCase(update_giveaway.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(update_giveaway.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Yeniləndi'
                s.currentGiveaway = payload.giveaway || s.currentGiveaway
                const idx = s.giveaways.findIndex(g => g._id === payload.giveaway?._id)
                if (idx !== -1) s.giveaways[idx] = payload.giveaway
            })
            .addCase(update_giveaway.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Yenilənmədi'
            })

            .addCase(delete_giveaway.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Silindi'
                s.giveaways = s.giveaways.filter(g => g._id !== payload.id)
            })
            .addCase(delete_giveaway.rejected,  (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Silinmədi'
            })

            .addCase(update_giveaway_status.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Status yeniləndi'
                const idx = s.giveaways.findIndex(g => g._id === payload.giveaway?._id)
                if (idx !== -1) s.giveaways[idx] = payload.giveaway
                if (s.currentGiveaway?._id === payload.giveaway?._id) s.currentGiveaway = payload.giveaway
            })
            .addCase(update_giveaway_status.rejected,  (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Status yenilənmədi'
            })

            // ── Phase 6E ────────────────────────────────────────────
            .addCase(get_admin_giveaway_analytics.pending,   (s) => {
                s.platformAnalyticsLoader = true
                s.platformAnalyticsError = ''
            })
            .addCase(get_admin_giveaway_analytics.fulfilled, (s, { payload }) => {
                s.platformAnalyticsLoader = false
                s.platformAnalytics = payload.analytics || null
            })
            .addCase(get_admin_giveaway_analytics.rejected,  (s, { payload }) => {
                s.platformAnalyticsLoader = false
                s.platformAnalytics = null
                s.platformAnalyticsError = payload?.error || payload?.message || 'Analitika yüklənmədi'
            })

            // ── Phase 6G — dashboard widget summary ─────────────────
            .addCase(get_admin_giveaway_dashboard_summary.pending,   (s) => {
                s.dashboardAnalyticsLoader = true
                s.dashboardAnalyticsError = ''
            })
            .addCase(get_admin_giveaway_dashboard_summary.fulfilled, (s, { payload }) => {
                s.dashboardAnalyticsLoader = false
                s.dashboardAnalytics = payload || null
            })
            .addCase(get_admin_giveaway_dashboard_summary.rejected,  (s, { payload }) => {
                s.dashboardAnalyticsLoader = false
                s.dashboardAnalytics = null
                s.dashboardAnalyticsError = payload?.error || payload?.message || 'Çəkiliş analitikası yüklənmədi'
            })

            .addCase(approve_giveaway.fulfilled, (s, { payload }) => {
                s.successMessage = payload.message || 'Yeniləndi'
                const idx = s.giveaways.findIndex(g => g._id === payload.giveaway?._id)
                if (idx !== -1) s.giveaways[idx] = payload.giveaway
                if (s.currentGiveaway?._id === payload.giveaway?._id) s.currentGiveaway = payload.giveaway
            })
            .addCase(approve_giveaway.rejected, (s, { payload }) => {
                s.errorMessage = payload?.error || payload?.message || 'Əməliyyat alınmadı'
            })

            .addCase(get_giveaway_participants.pending,   (s) => { s.loader = true })
            .addCase(get_giveaway_participants.fulfilled, (s, { payload }) => {
                s.loader = false
                s.participants = payload.participants || []
                s.participantsTotal = payload.total || 0
            })
            .addCase(get_giveaway_participants.rejected,  (s) => { s.loader = false })

            .addCase(select_giveaway_winners.pending,   (s) => { s.submitLoader = true; s.errorMessage = '' })
            .addCase(select_giveaway_winners.fulfilled, (s, { payload }) => {
                s.submitLoader = false
                s.successMessage = payload.message || 'Qaliblər seçildi'
                s.winners = payload.winners || []
                if (s.currentGiveaway) {
                    s.currentGiveaway = { ...s.currentGiveaway, winners: payload.winners, status: payload.status || 'completed' }
                }
            })
            .addCase(select_giveaway_winners.rejected,  (s, { payload }) => {
                s.submitLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Qaliblər seçilmədi'
            })

            // ── Phase 6H — winner audit history + rollback ──────────
            .addCase(get_winner_history.pending,   (s) => { s.winnerHistoryLoader = true })
            .addCase(get_winner_history.fulfilled, (s, { payload }) => {
                s.winnerHistoryLoader = false
                s.winnerHistory = payload.history || []
                s.winnerHistoryTotal = payload.total || 0
                s.winnerHistoryPage = payload.page || 1
                s.winnerHistoryTotalPages = payload.totalPages || 1
            })
            .addCase(get_winner_history.rejected,  (s) => { s.winnerHistoryLoader = false })

            .addCase(rollback_winner_history.pending,   (s) => { s.rollbackLoader = true; s.errorMessage = '' })
            .addCase(rollback_winner_history.fulfilled, (s, { payload }) => {
                s.rollbackLoader = false
                s.successMessage = payload.message || 'Qalib seçimi yeniləndi'
                s.winners = payload.winners || []
                if (s.currentGiveaway) {
                    s.currentGiveaway = { ...s.currentGiveaway, winners: payload.winners, status: payload.status }
                }
            })
            .addCase(rollback_winner_history.rejected,  (s, { payload }) => {
                s.rollbackLoader = false
                s.errorMessage = payload?.error || payload?.message || 'Rollback alınmadı'
            })
    },
})

export const { messageClear, clearCurrent, clearPlatformAnalytics } = adminGiveawaySlice.actions
export default adminGiveawaySlice.reducer
